import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import { useViewMode, type ViewMode } from '@plato/app-core/hooks/use-view-mode';
import { UsersIcon } from 'lucide-react';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Menu, MenuDrillRow, MenuOptionRow } from './menu-primitives.tsx';

/** The view the user is currently in, for the collapsed "View as" row. */
function useCurrentViewName(): string {
    const { t } = useTranslation('common');
    const [viewMode] = useViewMode();
    return viewMode === 'MANAGER' ? t('view_switch__manager') : t('view_switch__sales_rep');
}

export type ViewAsRowProps = {
    onOpen: () => void;
};

/**
 * "View as … Manager" — the collapsed row on the settings level. Renders
 * nothing unless the manager experience is active (manager role), so it is
 * safe to mount unconditionally. Reps never see a view they cannot switch to.
 */
export function ViewAsRow({ onOpen }: ViewAsRowProps) {
    const { t } = useTranslation('common');
    const { enableManagerView } = useAppConfig();
    const currentViewName = useCurrentViewName();

    if (!enableManagerView) {
        return null;
    }

    return (
        <MenuDrillRow
            icon={UsersIcon}
            label={t('view_switch__view_as_label')}
            value={currentViewName}
            onOpen={onOpen}
        />
    );
}

export type ViewAsRowsProps = {
    /** Called after picking the view already in use — pops back a level. */
    onPicked?: () => void;
    /** Called after an actual switch, so the containing surface can dismiss. */
    onSwitched?: () => void;
};

/** The two views as options, check-marked on the current one. */
export function ViewAsRows({ onPicked, onSwitched }: ViewAsRowsProps) {
    const { t } = useTranslation('common');
    const navigate = useNavigate();
    const [viewMode, setViewMode] = useViewMode();

    const pick = useCallback(
        (next: ViewMode) => {
            if (next === viewMode) {
                onPicked?.();
                return;
            }
            setViewMode(next);
            void navigate('/', { replace: true });
            onSwitched?.();
        },
        [viewMode, setViewMode, navigate, onPicked, onSwitched],
    );

    return (
        <Menu className="p-0">
            <MenuOptionRow
                label={t('view_switch__manager')}
                selected={viewMode === 'MANAGER'}
                onSelect={() => {
                    pick('MANAGER');
                }}
            />
            <MenuOptionRow
                label={t('view_switch__sales_rep')}
                selected={viewMode === 'REP'}
                onSelect={() => {
                    pick('REP');
                }}
            />
        </Menu>
    );
}
